import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { BookingsPage } from './pages/BookingsPage';
import { MembershipPage } from './pages/MembershipPage';
import { CompanionsPage } from './pages/CompanionsPage';
import { SettingsPage } from './pages/SettingsPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { PaymentFailedPage } from './pages/PaymentFailedPage';
import { PaymentPendingPage } from './pages/PaymentPendingPage';
import { PaymentHistoryPage } from './pages/PaymentHistoryPage';
import { AdminPaymentsPage } from './pages/AdminPaymentsPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminPanelPage } from './pages/AdminPanelPage';
import { AdminKYCReviewPage } from './pages/AdminKYCReviewPage';
import { KYCVerificationPage } from './pages/KYCVerificationPage';
import { VerificationCenterPage } from './pages/VerificationCenterPage';
import { CompanionRegisterPage } from './pages/CompanionRegisterPage';
import { CompanionDashboardPage } from './pages/CompanionDashboardPage';
import { ClientSignupPage } from './pages/ClientSignupPage';
import { ClientLoginPage } from './pages/ClientLoginPage';
import { ClientHomePage } from './pages/ClientHomePage';
import { MyAccountPage } from './pages/MyAccountPage';
import { MyBookingsPage } from './pages/MyBookingsPage';
import { BookingDetailPage } from './pages/BookingDetailPage';
import { CompanionDetailPage } from './pages/CompanionDetailPage';
import { AdminRoute } from './components/admin/AdminRoute';
import {
  COMPANIONS_DATA,
  isDemoCompanionProfile,
  getDemoCompanionBookingMessage,
} from './data/companionsData';
import { useLanguage } from './i18n/LanguageContext';
import { getCurrentDateString, getCurrentTimeString } from './utils/dateUtils';
import { Companion, SearchFilterState, BookingDetails, UserProfile } from './types';
import { Navbar } from './components/Navbar';
import { HeroSection } from './components/HeroSection';
import { HowItWorksSection } from './components/HowItWorksSection';
import { PopularCompanionsSection } from './components/PopularCompanionsSection';
import { WhyChooseSection } from './components/WhyChooseSection';
import { AboutSection } from './components/AboutSection';
import { DownloadAppSection } from './components/DownloadAppSection';
import { UserReviewsSection } from './components/UserReviewsSection';
import { Footer } from './components/Footer';
import { CompanionDetailModal } from './components/CompanionDetailModal';
import { BookingConfirmationModal } from './components/BookingConfirmationModal';
import { AuthModal } from './components/AuthModal';
import { CompanionDashboardModal } from './components/CompanionDashboardModal';
import { ClientDashboardModal } from './components/ClientDashboardModal';
import { SafetyModal } from './components/SafetyModal';
import { MembershipModal } from './components/MembershipModal';
import { AboutModal } from './components/AboutModal';
import { WishlistDrawer } from './components/WishlistDrawer';
import { AppDownloadModal } from './components/AppDownloadModal';
import { NotificationToast } from './components/NotificationToast';
import { UserProfileModal } from './components/UserProfileModal';
import { ChooseServiceSection } from './components/ChooseServiceSection';
import {
  auth,
  signOutUser,
  saveWishlistItemToFirestore,
  removeWishlistItemFromFirestore,
  getWishlistFromFirestore,
  getUserProfileFromFirestore,
  subscribeToWishlist,
  subscribeToUserProfile,
} from './lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { realtimeSocket } from './lib/socket';
import { FirstOpenPermissionFlowModal } from './components/permissions/FirstOpenPermissionFlowModal';
import { AppPermissionsManagerModal } from './components/permissions/AppPermissionsManagerModal';
import { checkLivePermissions, isFirstLaunchPermissionNeeded } from './lib/permissions';
import { initNativeMobileApp } from './lib/nativeBridge';

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 150);
      }
    }
  }, [location]);
  const [activeTab, setActiveTab] = useState<string>('home');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilterState>({
    gender: 'all',
    city: 'all',
    date: getCurrentDateString(),
    time: getCurrentTimeString(),
  });

  // Modals & Drawers state
  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [activeBooking, setActiveBooking] = useState<BookingDetails | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [authAccountType, setAuthAccountType] = useState<'client' | 'companion'>('client');
  const [safetyModalOpen, setSafetyModalOpen] = useState(false);
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [wishlistDrawerOpen, setWishlistDrawerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalTab, setProfileModalTab] = useState<'profile' | 'wallet'>('profile');
  const [companionDashboardOpen, setCompanionDashboardOpen] = useState(false);
  const [clientDashboardOpen, setClientDashboardOpen] = useState(false);
  const [firstOpenPermissionsOpen, setFirstOpenPermissionsOpen] = useState(false);
  const [permissionManagerOpen, setPermissionManagerOpen] = useState(false);

  const [currentUser, setCurrentUser] = useState<string | null>(() => {
    try {
      return localStorage.getItem('pairup_user');
    } catch {
      return null;
    }
  });

  // Stored user profile data for dynamic avatar & wallet count in Navbar
  const [storedProfile, setStoredProfile] = useState<UserProfile | null>(() => {
    try {
      localStorage.removeItem('pairup_active_companion_jobs');
      const saved = localStorage.getItem('pairup_user_profile_data');
      if (saved) {
        const parsed: UserProfile = JSON.parse(saved);
        // Clear any previous demo balance or demo transactions so wallet starts strictly at 0
        const hasOldDemoTx = parsed.wallet?.transactions?.some(
          (t: any) => t.id === 'TXN-EARN-9821' || t.id === 'TXN-WTH-8812' || t.id === 'TXN-EARN-7419'
        );
        if (
          hasOldDemoTx ||
          parsed.wallet?.availableBalance === 9200 ||
          parsed.wallet?.availableBalance === 14200 ||
          parsed.wallet?.availableBalance === 18450 ||
          parsed.wallet?.pendingEscrow === 4250
        ) {
          parsed.wallet = {
            availableBalance: 0,
            totalEarned: 0,
            pendingEscrow: 0,
            completedMeetups: 0,
            transactions: []
          };
          localStorage.setItem('pairup_user_profile_data', JSON.stringify(parsed));
        }
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  });

  // AUTHORITATIVE COMPANION ACTIVATION RULE:
  // IF companion_payment_status == "Success" AND payment_verified == true
  // THEN companion_profile_status = "Active" ELSE "Inactive"
  // Payment না করলে: Search/Listing-এ Companion-এর profile দেখাবে না!
  const companions = useMemo<Companion[]>(() => {
    const baseList: Companion[] = COMPANIONS_DATA.map((c) => ({
      ...c,
      companion_profile_status: c.companion_profile_status || 'Active',
      membership_status: c.membership_status || 'ACTIVE',
      payment_status: c.payment_status || 'Success',
      payment_verified: c.payment_verified ?? true,
    }));

    // Only return companions whose profile is Active and verified (never inject untrusted localStorage profiles)
    return baseList.filter(
      (c) => c.companion_profile_status === 'Active' && c.payment_verified === true && c.payment_status === 'Success'
    );
  }, []);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'heart' | 'info'>('success');

  // Wishlist state (persisted locally)
  const [wishlist, setWishlist] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('pairup_wishlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('pairup_wishlist', JSON.stringify(wishlist));
    } catch (e) {
      console.error(e);
    }
  }, [wishlist]);

  // Native Mobile App lifecycle, status bar, and hardware back button
  useEffect(() => {
    initNativeMobileApp(() => {
      if (selectedCompanion) { setSelectedCompanion(null); return true; }
      if (activeBooking) { setActiveBooking(null); return true; }
      if (authModalOpen) { setAuthModalOpen(false); return true; }
      if (safetyModalOpen) { setSafetyModalOpen(false); return true; }
      if (membershipModalOpen) { setMembershipModalOpen(false); return true; }
      if (aboutModalOpen) { setAboutModalOpen(false); return true; }
      if (wishlistDrawerOpen) { setWishlistDrawerOpen(false); return true; }
      if (downloadModalOpen) { setDownloadModalOpen(false); return true; }
      if (profileModalOpen) { setProfileModalOpen(false); return true; }
      if (companionDashboardOpen) { setCompanionDashboardOpen(false); return true; }
      if (clientDashboardOpen) { setClientDashboardOpen(false); return true; }
      if (firstOpenPermissionsOpen) { setFirstOpenPermissionsOpen(false); return true; }
      if (permissionManagerOpen) { setPermissionManagerOpen(false); return true; }
      return false;
    });
  }, [
    selectedCompanion,
    activeBooking,
    authModalOpen,
    safetyModalOpen,
    membershipModalOpen,
    aboutModalOpen,
    wishlistDrawerOpen,
    downloadModalOpen,
    profileModalOpen,
    companionDashboardOpen,
    clientDashboardOpen,
    firstOpenPermissionsOpen,
    permissionManagerOpen,
  ]);

  // Listen for Firebase Auth state changes and sync cloud state in real-time
  useEffect(() => {
    let unsubscribeWishlist: (() => void) | null = null;
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubscribeWishlist) {
        unsubscribeWishlist();
        unsubscribeWishlist = null;
      }
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (user) {
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setCurrentUser(displayName);

        // Real-time wishlist listener
        unsubscribeWishlist = subscribeToWishlist(user.uid, (cloudWishlist) => {
          if (cloudWishlist && cloudWishlist.length > 0) {
            setWishlist((prev) => Array.from(new Set([...prev, ...cloudWishlist])));
          }
        });

        // Real-time profile listener (sync authoritative cloud profile)
        unsubscribeProfile = subscribeToUserProfile(user.uid, (cloudProfile) => {
          if (cloudProfile) {
            setStoredProfile((prev) => ({
              ...prev,
              uid: user.uid,
              avatarUrl: cloudProfile.avatarUrl || prev.avatarUrl,
              isIdVerified: cloudProfile.isVerified === true,
              companion_profile_status: cloudProfile.companion_profile_status || prev.companion_profile_status,
              membership_status: cloudProfile.membership_status || prev.membership_status,
              payment_status: cloudProfile.payment_status || prev.payment_status,
              payment_verified: cloudProfile.payment_verified ?? prev.payment_verified,
            }));
          }
        });
      }
    });

    return () => {
      if (unsubscribeWishlist) unsubscribeWishlist();
      if (unsubscribeProfile) unsubscribeProfile();
      unsubscribeAuth();
    };
  }, []);

  // Listen to realtime server notifications & booking status updates
  useEffect(() => {
    const unsubBooking = realtimeSocket.subscribe('BOOKING_STATUS_CHANGED', (booking: any) => {
      if (booking?.status === 'COMPANION_ACCEPTED') {
        showToast(`🎉 Companion ${booking.companion_name} accepted the booking!`, 'success');
      } else if (booking?.status === 'COMPLETED') {
        showToast(`✓ Booking #${booking.id} completed!`, 'success');
      }
    });

    const unsubNotif = realtimeSocket.subscribe('NOTIFICATION', (notif: any) => {
      if (notif?.title) {
        showToast(`${notif.title}: ${notif.message}`, 'info');
      }
    });

    return () => {
      unsubBooking();
      unsubNotif();
    };
  }, []);

  // Passive permission status check without intrusive startup modals
  // Adheres to PairUpToday's strict privacy policy: permissions are only requested on-demand
  useEffect(() => {
    checkLivePermissions().catch(console.warn);
  }, []);

  const showToast = (msg: string, type: 'success' | 'heart' | 'info' = 'success') => {
    setToastMessage(msg);
    setToastType(type);
    setTimeout(() => {
      setToastMessage((current) => (current === msg ? null : current));
    }, 4000);
  };

  const handleToggleWishlist = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const comp = companions.find((c) => c.id === id);
    if (wishlist.includes(id)) {
      setWishlist((prev) => prev.filter((item) => item !== id));
      if (auth.currentUser) {
        removeWishlistItemFromFirestore(auth.currentUser.uid, id).catch(console.warn);
      }
      showToast(`Removed ${comp?.name || 'companion'} from your saved favorites`, 'info');
    } else {
      setWishlist((prev) => [...prev, id]);
      if (auth.currentUser) {
        saveWishlistItemToFirestore(auth.currentUser.uid, id).catch(console.warn);
      }
      showToast(`Added ${comp?.name || 'companion'} to your saved favorites 💕`, 'heart');
    }
  };

  const handleQuickGenderSelect = (gender: 'male' | 'female') => {
    setFilters((prev) => ({ ...prev, gender }));
    const el = document.getElementById('companions-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    showToast(`Showing verified ${gender === 'female' ? 'girlfriends' : 'boyfriends'} for hire`, 'info');
  };

  const handleSearchTrigger = () => {
    const el = document.getElementById('companions-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    showToast('Updated companion search results!', 'success');
  };

  const handleBookNow = (companion: Companion, e: React.MouseEvent, serviceName?: string) => {
    e.stopPropagation();
    if (isDemoCompanionProfile(companion)) {
      showToast(getDemoCompanionBookingMessage(language), 'info');
      if (serviceName) {
        setSelectedService(serviceName);
      }
      setSelectedCompanion(companion);
      return;
    }
    // Rule: Inactive companion profiles cannot accept bookings
    if (
      companion.companion_profile_status !== 'Active' ||
      companion.payment_verified !== true ||
      companion.payment_status !== 'Success'
    ) {
      showToast('⚠️ Booking blocked: This companion profile is currently INACTIVE (Payment Unverified).', 'info');
      return;
    }
    if (serviceName) {
      setSelectedService(serviceName);
    }
    setSelectedCompanion(companion);
  };

  const handleSelectCompanion = (companion: Companion, serviceName?: string) => {
    if (serviceName) {
      setSelectedService(serviceName);
    }
    setSelectedCompanion(companion);
  };

  const handleProceedToBooking = (bookingDetails: BookingDetails) => {
    if (isDemoCompanionProfile(bookingDetails.companion)) {
      showToast(getDemoCompanionBookingMessage(language), 'info');
      return;
    }
    if (
      bookingDetails.companion.companion_profile_status !== 'Active' ||
      bookingDetails.companion.payment_verified !== true ||
      bookingDetails.companion.payment_status !== 'Success'
    ) {
      showToast('⚠️ Booking blocked: This companion profile is currently INACTIVE.', 'info');
      return;
    }
    setSelectedCompanion(null);
    setActiveBooking(bookingDetails);
  };

  const handleBookingSuccess = () => {
    showToast(`🎉 Booking confirmed with ${activeBooking?.companion.name}! Your OTP has been generated.`, 'success');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    signOutUser().catch(console.warn);
    try {
      localStorage.removeItem('pairup_user');
    } catch (e) {
      console.error(e);
    }
    showToast('You have been safely logged out', 'info');
  };

  const handleCreditEarnings = (
    _amount: number,
    details: { title: string; clientOrBeneficiary: string; meetOtp?: string; serviceCategory?: string }
  ) => {
    void details;
    // Wallet settlement is authoritative on the backend (companions/{uid}); never fabricate localStorage wallet balances.
    showToast('Meetup verified! Payout settlement is recorded on the authoritative backend ledger.', 'success');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans text-slate-900 selection:bg-pink-500 selection:text-white">
      
      {/* Route Switcher */}
      <Routes>
        <Route
          path="/"
          element={
            <>
              {/* 1. Header & Navigation */}
              <Navbar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                currentUser={currentUser}
                onLogout={handleLogout}
                onOpenAuth={(mode, accountType = 'client') => {
                  setAuthMode(mode);
                  setAuthAccountType(accountType);
                  setAuthModalOpen(true);
                }}
                onOpenWishlist={() => setWishlistDrawerOpen(true)}
                wishlistCount={wishlist.length}
                onOpenSafety={() => setSafetyModalOpen(true)}
                onOpenMembership={() => navigate('/membership')}
                onOpenAbout={() => setAboutModalOpen(true)}
                onOpenProfile={(tab) => {
                  if (!currentUser) {
                    setAuthMode('login');
                    setAuthModalOpen(true);
                    showToast('Please log in first to view your account', 'info');
                    return;
                  }
                  navigate('/profile');
                }}
                userAvatarUrl={storedProfile?.avatarUrl}
                walletBalance={storedProfile?.wallet?.availableBalance ?? 0}
                onOpenPermissions={() => setPermissionManagerOpen(true)}
              />

              {/* Main Content Sections matching exact visual design */}
              <main className="flex-1">
                {/* 2. Hero Section with Search & Trust Badges */}
                <HeroSection
                  filters={filters}
                  setFilters={setFilters}
                  onSearch={handleSearchTrigger}
                  onSelectGenderQuick={handleQuickGenderSelect}
                  onOpenSafety={() => setSafetyModalOpen(true)}
                  onSelectService={(srv) => {
                    setSelectedService(srv);
                    setFilters((prev) => ({ ...prev, service: srv || undefined }));
                    showToast(`Showing available companions for ${srv} ✨`, 'info');
                  }}
                />

                {/* 3. Choose a Service Section */}
                <ChooseServiceSection
                  companions={companions}
                  selectedService={selectedService}
                  onSelectService={(srv) => {
                    setSelectedService(srv);
                    setFilters((prev) => ({ ...prev, service: srv || undefined }));
                    if (srv) {
                      showToast(`Showing available companions for ${srv} ✨`, 'info');
                    }
                  }}
                  wishlist={wishlist}
                  onToggleWishlist={handleToggleWishlist}
                  onSelectCompanion={handleSelectCompanion}
                  onBookNow={handleBookNow}
                />

                {/* 4. How It Works Section */}
                <HowItWorksSection
                  onStepClick={(step) => {
                    if (step === 1) {
                      setAuthMode('signup');
                      setAuthAccountType('client');
                      setAuthModalOpen(true);
                    } else if (step === 2) {
                      navigate('/companions');
                    } else if (step === 3) {
                      if (companions[0]) setSelectedCompanion(companions[0]);
                    } else {
                      setSafetyModalOpen(true);
                    }
                  }}
                />

                {/* 4. Popular Companions Section */}
                <PopularCompanionsSection
                  companions={companions}
                  filters={filters}
                  setFilters={setFilters}
                  wishlist={wishlist}
                  onToggleWishlist={handleToggleWishlist}
                  onSelectCompanion={(comp) => setSelectedCompanion(comp)}
                  onBookNow={handleBookNow}
                />

                {/* 5. Why Choose PairUpToday Section */}
                <WhyChooseSection
                  onOpenSafety={() => setSafetyModalOpen(true)}
                />

                {/* 6. About PairUpToday Section */}
                <AboutSection
                  onOpenSafety={() => setSafetyModalOpen(true)}
                  onExploreCompanions={() => navigate('/companions')}
                />

                {/* 7. Download Our App Section */}
                <DownloadAppSection
                  onShowQRModal={() => setDownloadModalOpen(true)}
                />

                {/* 8. What Our Users Say Section */}
                <UserReviewsSection />

              </main>

              {/* 9. Footer Section */}
              <Footer
                onNavClick={(tab) => {
                  setActiveTab(tab);
                  if (tab === 'home') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else if (tab === 'about') {
                    document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' });
                  } else if (tab === 'how-it-works') {
                    document.getElementById('how-it-works-section')?.scrollIntoView({ behavior: 'smooth' });
                  } else if (tab === 'browse') {
                    navigate('/companions');
                  } else if (tab === 'blog') {
                    document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                onOpenSafety={() => setSafetyModalOpen(true)}
                onOpenMembership={() => navigate('/membership')}
                onOpenAbout={() => setAboutModalOpen(true)}
                onOpenPermissions={() => setPermissionManagerOpen(true)}
                currentUser={currentUser}
                onOpenAuth={(mode) => {
                  setAuthMode(mode);
                  setAuthAccountType('client');
                  setAuthModalOpen(true);
                }}
                onOpenProfile={() => {
                  if (!currentUser) {
                    setAuthMode('login');
                    setAuthModalOpen(true);
                    showToast('Please log in first to view your profile', 'info');
                    return;
                  }
                  navigate('/profile');
                }}
              />
            </>
          }
        />

        {/* Dedicated Route Pages */}
        <Route path="/home" element={<ClientHomePage />} />
        <Route path="/login" element={<ClientLoginPage />} />
        <Route path="/signup" element={<ClientSignupPage />} />
        <Route path="/my-account" element={<MyAccountPage />} />
        <Route path="/my-bookings" element={<MyBookingsPage />} />
        <Route path="/booking/:bookingId" element={<BookingDetailPage />} />
        <Route path="/companions/:companionId" element={<CompanionDetailPage />} />
        <Route path="/companion/register" element={<CompanionRegisterPage />} />
        <Route path="/companion/dashboard" element={<CompanionDashboardPage />} />
        <Route path="/companion/login" element={<ClientLoginPage />} />
        <Route path="/companion-login" element={<ClientLoginPage />} />
        <Route path="/client/login" element={<ClientLoginPage />} />
        <Route path="/client-login" element={<ClientLoginPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin-login" element={<AdminLoginPage />} />
        <Route
          path="/admin/kyc"
          element={
            <AdminRoute>
              <AdminKYCReviewPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPanelPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <AdminRoute>
              <AdminPanelPage />
            </AdminRoute>
          }
        />
        <Route path="/kyc" element={<KYCVerificationPage />} />
        <Route path="/kyc-verification" element={<KYCVerificationPage />} />
        <Route path="/verification" element={<VerificationCenterPage />} />
        <Route path="/verification-center" element={<VerificationCenterPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/profile" element={<MyAccountPage />} />
        <Route path="/bookings" element={<MyBookingsPage />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/payment-failed" element={<PaymentFailedPage />} />
        <Route path="/payment-pending" element={<PaymentPendingPage />} />
        <Route path="/payment-history" element={<PaymentHistoryPage />} />
        <Route path="/companions" element={<CompanionsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Interactive Modals & Drawers */}
      {selectedCompanion && (
        <CompanionDetailModal
          companion={selectedCompanion}
          onClose={() => setSelectedCompanion(null)}
          isWishlisted={wishlist.includes(selectedCompanion.id)}
          onToggleWishlist={handleToggleWishlist}
          onProceedToBooking={handleProceedToBooking}
          initialOccasion={selectedService || undefined}
        />
      )}

      {activeBooking && (
        <BookingConfirmationModal
          booking={activeBooking}
          onClose={() => setActiveBooking(null)}
          onSuccess={handleBookingSuccess}
          onCreditEarnings={handleCreditEarnings}
        />
      )}

      {authModalOpen && (
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          initialMode={authMode}
          initialAccountType={authAccountType}
          onLoginSuccess={(name, accountType, profileData, membership) => {
            setCurrentUser(name);
            try {
              localStorage.setItem('pairup_user', name);
              if (profileData) {
                setStoredProfile(profileData);
              } else {
                const saved = localStorage.getItem('pairup_user_profile_data');
                if (saved) setStoredProfile(JSON.parse(saved));
              }
            } catch (e) {
              console.error(e);
            }
            if (accountType === 'companion') {
              showToast(`Welcome, ${name}! Your Companion account is active 🎉`, 'success');
            } else {
              showToast(`Welcome, ${name}! Your free client profile is active 🎉`, 'success');
            }
          }}
          onOpenCompanionDashboard={() => {
            setAuthModalOpen(false);
            navigate('/dashboard');
          }}
          onOpenClientDashboard={() => {
            setAuthModalOpen(false);
            navigate('/dashboard');
          }}
          onFindCompanion={() => {
            setAuthModalOpen(false);
            navigate('/companions');
          }}
          onOpenBookings={() => {
            setAuthModalOpen(false);
            navigate('/bookings');
          }}
          onOpenProfile={() => {
            setAuthModalOpen(false);
            navigate('/profile');
          }}
          onOpenWallet={() => {
            setAuthModalOpen(false);
            navigate('/dashboard');
          }}
        />
      )}

      {companionDashboardOpen && (
        <CompanionDashboardModal
          isOpen={companionDashboardOpen}
          onClose={() => setCompanionDashboardOpen(false)}
          userProfile={storedProfile as any}
          onUpdateProfile={(updated) => {
            setStoredProfile((prev: any) => ({ ...prev, ...updated }));
            try {
              const saved = localStorage.getItem('pairup_user_profile_data');
              const parsed = saved ? JSON.parse(saved) : {};
              localStorage.setItem('pairup_user_profile_data', JSON.stringify({ ...parsed, ...updated }));
            } catch (e) {
              console.error(e);
            }
          }}
          onOpenMembershipModal={() => {
            setCompanionDashboardOpen(false);
            navigate('/membership');
          }}
          onOpenProfileEditor={() => {
            setCompanionDashboardOpen(false);
            navigate('/profile');
          }}
        />
      )}

      {clientDashboardOpen && (
        <ClientDashboardModal
          isOpen={clientDashboardOpen}
          onClose={() => setClientDashboardOpen(false)}
          userProfile={storedProfile as any}
          onFindCompanion={() => {
            setClientDashboardOpen(false);
            navigate('/companions');
          }}
          onOpenBookings={() => {
            setClientDashboardOpen(false);
            navigate('/bookings');
          }}
          onOpenProfile={() => {
            setClientDashboardOpen(false);
            navigate('/profile');
          }}
          onOpenWallet={() => {
            setClientDashboardOpen(false);
            navigate('/dashboard');
          }}
        />
      )}

      {safetyModalOpen && (
        <SafetyModal
          isOpen={safetyModalOpen}
          onClose={() => setSafetyModalOpen(false)}
        />
      )}

      {aboutModalOpen && (
        <AboutModal
          isOpen={aboutModalOpen}
          onClose={() => setAboutModalOpen(false)}
          onOpenSafety={() => setSafetyModalOpen(true)}
          onExploreCompanions={() => {
            setAboutModalOpen(false);
            const el = document.getElementById('companions-section');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
        />
      )}

      {membershipModalOpen && (
        <MembershipModal
          isOpen={membershipModalOpen}
          onClose={() => setMembershipModalOpen(false)}
          onPlanActivated={(plan) => {
            showToast(`Selected ${plan}! Welcome to VIP status.`, 'success');
          }}
          onGoToDashboard={() => {
            setMembershipModalOpen(false);
            navigate('/dashboard');
          }}
        />
      )}

      {wishlistDrawerOpen && (
        <WishlistDrawer
          isOpen={wishlistDrawerOpen}
          onClose={() => setWishlistDrawerOpen(false)}
          wishlistIds={wishlist}
          allCompanions={companions}
          onRemove={handleToggleWishlist}
          onSelectCompanion={(comp) => {
            setSelectedCompanion(comp);
          }}
        />
      )}

      {downloadModalOpen && (
        <AppDownloadModal
          isOpen={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
        />
      )}

      {/* User Profile & Work Earnings Wallet Modal */}
      {profileModalOpen && (
        <UserProfileModal
          isOpen={profileModalOpen}
          onClose={() => {
            setProfileModalOpen(false);
            // Sync storedProfile state when closed so navbar updates immediately
            try {
              const saved = localStorage.getItem('pairup_user_profile_data');
              if (saved) setStoredProfile(JSON.parse(saved));
            } catch (e) {
              console.error(e);
            }
          }}
          currentUser={currentUser}
          onUpdateCurrentUser={(name) => {
            setCurrentUser(name);
            try {
              localStorage.setItem('pairup_user', name);
              const saved = localStorage.getItem('pairup_user_profile_data');
              if (saved) setStoredProfile(JSON.parse(saved));
            } catch (e) {
              console.error(e);
            }
          }}
          onShowToast={showToast}
          initialTab={profileModalTab}
          onProfileUpdate={(updated) => setStoredProfile(updated)}
        />
      )}

      {/* First App Open Permission Flow Modal */}
      {firstOpenPermissionsOpen && (
        <FirstOpenPermissionFlowModal
          isOpen={firstOpenPermissionsOpen}
          onComplete={() => setFirstOpenPermissionsOpen(false)}
          onOpenLogin={() => {
            setFirstOpenPermissionsOpen(false);
            setAuthMode('login');
            setAuthAccountType('client');
            setAuthModalOpen(true);
          }}
        />
      )}

      {/* Device Permissions Center Modal */}
      {permissionManagerOpen && (
        <AppPermissionsManagerModal
          isOpen={permissionManagerOpen}
          onClose={() => setPermissionManagerOpen(false)}
          onRestartOnboarding={() => setFirstOpenPermissionsOpen(true)}
        />
      )}

      {/* Floating Notification Toast */}
      {toastMessage && (
        <NotificationToast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
        />
      )}

    </div>
  );
}
